import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { 
  User,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Wifi,
  WifiOff,
  RefreshCw,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { Button } from '../../../components/shared';
import DataTable from '../../../components/shared/DataTable';
import ClientStats from '../../../components/admin/ClientStats';
import { 
  useClients, 
  useClientStats
} from '../../../services/clientService';
import { clientKeys } from '../../../services/clientService';
import { AdminPageHeader, AdminGuideCallout } from '../../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../../utils/adminPageGuides';

const AdminClients = () => {
  const [currentPage, setCurrentPage] = useState(1);
  
  const queryClient = useQueryClient();

  const queryParams = {
    sort_by: 'created_at',
    sort_order: 'desc',
    page: currentPage
  };

  const { 
    data: clientsData, 
    isLoading: loading, 
    error: clientsError,
    refetch 
  } = useClients(queryParams);

  const { 
    data: statsData, 
    isLoading: statsLoading
  } = useClientStats();

  const clients = clientsData?.data || [];
  const pagination = clientsData?.pagination || null;

  const totalClients = statsData?.data?.total_clients || 0;
  const activeClients = statsData?.data?.active_clients || 0;
  const inactiveClients = statsData?.data?.inactive_clients || 0;
  const verifiedClients = statsData?.data?.verified_clients || 0;

  const isClientOnline = (client) => {
    if (client.is_online !== undefined) {
      return client.is_online;
    }
    
    if (!client.last_logged_in) return false;
    
    const lastLoginTime = new Date(client.last_logged_in);
    const now = new Date();
    const timeDiff = now - lastLoginTime;
    
    const fiveMinutes = 5 * 60 * 1000;
    return timeDiff < fiveMinutes;
  };

  const getTimeSinceLastActivity = (client) => {
    const activityTime = client.last_activity || client.last_logged_in;
    
    if (!activityTime) return "Never";
    
    const activityDate = new Date(activityTime);
    const now = new Date();
    const timeDiff = now - activityDate;
    
    const minutes = Math.floor(timeDiff / (1000 * 60));
    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  useEffect(() => {
    const handleLogout = () => {
      console.log('User logged out, refreshing client data...');
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    };

    window.addEventListener('userLoggedOut', handleLogout);

    return () => {
      window.removeEventListener('userLoggedOut', handleLogout);
    };
  }, [queryClient]);

  const columns = [
    {
      key: 'name',
      label: 'Client',
      sortable: true,
      searchable: true,
      mobilePrimary: true,
      exportValue: (client) =>
        [client.name || 'No Name', client.email].filter(Boolean).join(' · '),
      render: (client) => (
        <div className="flex items-center min-w-0">
          <div className="flex-shrink-0 h-10 w-10">
            {client.avatar ? (
              <img
                className="h-10 w-10 rounded-full object-cover"
                src={client.avatar}
                alt={client.name}
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <User className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              </div>
            )}
          </div>
          <div className="ml-3 sm:ml-4 min-w-0">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">
              {client.name || 'No Name'}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{client.email}</div>
          </div>
        </div>
      )
    },
    {
      key: 'phone',
      label: 'Phone',
      sortable: true,
      searchable: true,
      exportValue: (client) => client.phone || 'N/A',
      render: (client) => client.phone || 'N/A'
    },
    {
      key: 'user_type',
      label: 'Type',
      sortable: true,
      exportValue: (client) => client.user_type || 'client',
      render: (client) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300">
          {client.user_type || 'client'}
        </span>
      )
    },
    {
      key: 'is_active',
      label: 'Account Status',
      sortable: true,
      exportValue: (client) => (client.is_active ? 'Active' : 'Inactive'),
      render: (client) => (
        <div className="flex items-center justify-end lg:justify-start">
          {client.is_active ? (
            <CheckCircle className="h-4 w-4 text-emerald-500 mr-2" />
          ) : (
            <XCircle className="h-4 w-4 text-red-500 mr-2" />
          )}
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            client.is_active 
              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300' 
              : 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300'
          }`}>
            {client.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      )
    },
    {
      key: 'online_status',
      label: 'Online Status',
      sortable: true,
      exportValue: (client) =>
        `${isClientOnline(client) ? 'Online' : 'Offline'} (${getTimeSinceLastActivity(client)})`,
      render: (client) => {
        const isOnline = isClientOnline(client);
        const timeAgo = getTimeSinceLastActivity(client);
        
        return (
          <div 
            className="flex items-center text-sm min-w-0 cursor-help justify-end lg:justify-start" 
            title={`Status: ${isOnline ? 'Online' : 'Offline'} | Last activity: ${client.last_activity ? new Date(client.last_activity).toLocaleString() : client.last_logged_in ? new Date(client.last_logged_in).toLocaleString() : 'Never'}${client.country ? ` | Location: ${client.country}` : ''}${client.ip_address ? ` | IP: ${client.ip_address}` : ''}`}
          >
            <div className="relative flex items-center">
              <div className="relative">
                {isOnline ? (
                  <div className="relative">
                    <div className="absolute -top-1 -left-1 w-3 h-3 bg-emerald-400 rounded-full animate-ping opacity-75"></div>
                    <div className="absolute -top-1 -left-1 w-3 h-3 bg-emerald-300 rounded-full animate-ping opacity-50" style={{ animationDelay: '0.5s' }}></div>
                    <div className="relative w-2 h-2 bg-emerald-500 rounded-full shadow-sm"></div>
                  </div>
                ) : (
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                )}
              </div>
              
              <div className="ml-2 flex items-center">
                {isOnline ? (
                  <Wifi className="w-3 h-3 text-emerald-500 mr-1" />
                ) : (
                  <WifiOff className="w-3 h-3 text-gray-400 mr-1" />
                )}
                <div className="flex flex-col items-end lg:items-start">
                  <span className={`text-xs font-medium ${
                    isOnline ? 'text-emerald-700 dark:text-emerald-400' : 'text-gray-600 dark:text-gray-400'
                  }`}>
                    {isOnline ? 'Online' : 'Offline'}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {timeAgo}
                  </span>
                  {client.country && (
                    <span className="text-xs text-gray-400 truncate max-w-16">
                      {client.country_code || client.country}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'created_at',
      label: 'Joined',
      sortable: true,
      exportValue: (client) => {
        if (!client.created_at) return '';
        return new Date(client.created_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      },
      render: (client) => {
        const date = new Date(client.created_at);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    }
  ];

  const actions = [
    {
      label: 'View',
      icon: Eye,
      onClick: (client) => {
        window.location.href = `/admin/clients/${client.id}`;
      },
      title: 'View Client Details',
      variant: 'default'
    }
  ];

  const handleSearch = (data, searchTerm) => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return data;

    return data.filter((client) => {
      const haystack = [
        client.name,
        client.email,
        client.phone,
        client.user_type,
        client.country,
        client.country_code,
        client.is_active ? 'active' : 'inactive',
        isClientOnline(client) ? 'online' : 'offline',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(term);
    });
  };

  if (clientsError) {
    return (
      <div className="space-y-5">
        <AdminPageHeader
          title="Clients"
          subtitle="Manage client accounts and information"
        />
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-white dark:bg-gray-900 p-8 text-center shadow-sm">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-gray-50">Error Loading Clients</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {clientsError.message || 'Failed to load clients'}
          </p>
          <div className="mt-6">
            <Button className="!rounded-xl" onClick={() => refetch()}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Clients"
        subtitle="Manage client accounts and information"
        actions={
          <Button
            variant="outline"
            className="!rounded-xl"
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: clientKeys.all });
            }}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.clients.title}
        steps={ADMIN_PAGE_GUIDES.clients.steps}
        icon={ListChecks}
      />

      {(loading || statsLoading) && !clientsData ? (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm py-20 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading clients…</p>
        </div>
      ) : (
        <>
          <ClientStats
            totalClients={totalClients}
            activeClients={activeClients}
            inactiveClients={inactiveClients}
            verifiedClients={verifiedClients}
          />

          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
            <div className="p-4 sm:p-5">
              <DataTable
                data={clients}
                columns={columns}
                actions={actions}
                loading={loading}
                searchable={true}
                filterable={false}
                sortable={true}
                selectable={false}
                exportable={true}
                exportFileName="clients"
                onSearch={handleSearch}
                pageSize={pagination?.per_page || 15}
                showPagination={false}
                searchPlaceholder="Search by name, email, phone, status…"
                emptyMessage="No clients found. Get started by adding a new client."
                className="!border-0 !shadow-none"
              />
            </div>

            {pagination && pagination.last_page > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-700 dark:text-gray-300 text-center sm:text-left">
                    Showing {pagination.from || 1} to {pagination.to || clients.length} of {pagination.total} results
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (pagination.current_page > 1) {
                          setCurrentPage(pagination.current_page - 1);
                        }
                      }}
                      disabled={pagination.current_page === 1}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 !rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200"
                    >
                      <span className="hidden sm:inline">Previous</span>
                      <span className="sm:hidden">Prev</span>
                    </button>
                    
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <button
                            key={page}
                            type="button"
                            onClick={() => {
                              setCurrentPage(page);
                            }}
                            className={`px-3 py-1.5 text-sm border !rounded-xl ${
                              pagination.current_page === page
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      type="button"
                      onClick={() => {
                        if (pagination.current_page < pagination.last_page) {
                          setCurrentPage(pagination.current_page + 1);
                        }
                      }}
                      disabled={pagination.current_page === pagination.last_page}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 !rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200"
                    >
                      <span className="hidden sm:inline">Next</span>
                      <span className="sm:hidden">Next</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminClients;
